/**
 * TicketsPage — list of the caller's support tickets.
 *
 * Filters by status. New-ticket button leads to `/dashboard/help/tickets/new`.
 * Clicking a row opens `/dashboard/help/tickets/:id`.
 */
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { DashboardHeader } from '@/src/modules/components/Header'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import {
  listMyTickets,
  type ApiSupportTicket,
  type SupportTicketStatus,
} from '@/src/infrastructure/lib/api'
import {
  TICKET_PRIORITY_TONE,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
} from './constants'

const STATUS_FILTERS: { value: 'ALL' | SupportTicketStatus; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'WAITING_FOR_USER', label: 'Needs your reply' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
]

function formatRelative(iso: string) {
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

export function TicketsPage() {
  const [items, setItems] = useState<ApiSupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState<'ALL' | SupportTicketStatus>('ALL')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    listMyTickets({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      limit: 50,
    })
      .then((page) => !cancelled && setItems(page.items))
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [statusFilter])

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 space-y-6 max-w-4xl mx-auto">
      <DashboardHeader
        title="My support tickets"
        description="Everything you've sent us — sorted by latest activity."
      />

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              aria-pressed={statusFilter === f.value}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                statusFilter === f.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline-variant/40 text-on-surface-variant hover:text-on-surface',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <Link
          to="/dashboard/help/tickets/new"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-deep"
        >
          <Icons.Plus size={14} /> New ticket
        </Link>
      </header>

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-on-surface-variant">Loading tickets…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-outline-variant/50 p-10 text-center">
          <p className="text-sm text-on-surface-variant">
            No tickets here.{' '}
            <Link
              to="/dashboard/help/tickets/new"
              className="font-semibold text-primary hover:underline"
            >
              Open your first ticket
            </Link>
            .
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((t) => (
            <motion.li
              key={t.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link
                to={`/dashboard/help/tickets/${t.id}`}
                className="block rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-4 transition-colors hover:border-primary/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant">
                      {t.category} · #{t.id.slice(0, 8)}
                    </p>
                    <h3 className="mt-1 font-semibold text-on-surface truncate">
                      {t.subject}
                    </h3>
                    <p className="mt-1 text-xs text-on-surface-variant line-clamp-2">
                      {t.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                        TICKET_STATUS_TONE[t.status],
                      )}
                    >
                      {TICKET_STATUS_LABEL[t.status]}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-semibold uppercase',
                        TICKET_PRIORITY_TONE[t.priority],
                      )}
                    >
                      {t.priority}
                    </span>
                  </div>
                </div>
                <footer className="mt-3 flex items-center justify-between text-[11px] text-on-surface-variant">
                  <span>
                    {t.messageCount} {t.messageCount === 1 ? 'message' : 'messages'}
                  </span>
                  <span>Updated {formatRelative(t.lastActivityAt)}</span>
                </footer>
              </Link>
            </motion.li>
          ))}
        </ul>
      )}
    </div>
  )
}
